import LoadFailure from '../../../components/Brand/LoadFailure';
import { storageName } from '../../../utils/storage';
import { ChevronUpIcon, ChevronDownIcon } from '../../../components/Brand/Icon';
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { Button, Layout } from 'antd';
import message from '../../../components/Brand/brandMessage';
import Dexie from 'dexie';
import { Helmet } from "react-helmet";
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import api from '../../../services';
import { getLocalStorage, getUser } from '../../../utils/func';
import { APP_ROUTES } from '../../../utils/constant';
import config from '../../../utils/config';
import url from '../../../utils/url';
import PostFilter from '../../../components/PostFilter';
import WiwHeaderTitle from '../../../components/WiwHeaderTitle';
import { opinionPath } from '../../../utils/opinionPath';
import { createProfileFeed } from './profileFeed';
import StoryTitle from '../../../components/StoryTitle';
import ReactionContent from '../ReactionContent';
import ButtonPanelReaction from '../../../components/Buttons/ButtonPanelReaction';
import BrandLoader from '../../../components/Brand/BrandLoader';
import AppFailure from '../../../components/Brand/AppFailure';
import { useTranslation } from 'react-i18next';
import usePageLayout from '../../../hooks/usePageLayout';
import { canStartSwipe, getSwipeDirection } from './swipeGesture';
import { rememberFeedForSearch, recallFeedFromSearch, feedViewerContext } from '../../../utils/searchFeedReturn';
import { createFeedStoryPreview, readFeedStoryPreview } from '../../../utils/feedStoryPreview';
import { readReactionReader } from '../../../utils/reactionReaderState';
import { useOnboarding } from '../../../onboarding/OnboardingProvider';
import { readProgress, saveProgress } from '../../../onboarding/state';
import OnboardingCard, { lessonCards, mutedProps } from '../../../onboarding/OnboardingCard';
import { routePath } from '../../../appLinks/links';
import changeLanguageInOrder from '../../../utils/changeLanguageInOrder';
import { unsubscribeCard, UNSUBSCRIBE_CARD_ID } from '../../../unsubscribe/card';
import './ReactionLine.css';

const { Header, Content, Footer } = Layout;
const {
  reactionLoad, // загрузка новости по id
  reactionsLoad, // фоновая загрузка загрузка новостей
  reactionsLoadRefresh, // первоначальная загрузка новостей на главной со сбросом загруженных новостей
  reactionView
} = api;

const db = new Dexie(storageName('NewsDatabase'));
db.version(1).stores({
  news: 'id, rang, viewed, viewedAt'
});

// Adjacent cards are derived from the current buffer. Keeping another state
// copy added renders and allowed a just-refreshed buffer to have stale arrows.
const adjacentNewsFor = (current, viewed, unViewed) => {
  if (!current) return { next: null, prev: null };
  const viewedIndex = viewed.findIndex(item => item.id === current.id);
  if (viewedIndex !== -1) return {
    next: viewed[viewedIndex + 1] || unViewed[0] || null,
    prev: viewed[viewedIndex - 1] || null,
  };
  const index = unViewed.findIndex(item => item.id === current.id);
  return index === -1 ? { next: null, prev: null } : {
    next: unViewed[index === 0 ? 1 : 0] || null,
    prev: viewed[viewed.length - 1] || null,
  };
};

// Адаптер структуры реакции
const adaptReactionItem = (item) => ({
  id: item.id,
  story: item.story,
  scope: item.scope,
  language: item.language,
  user: item.user,
  text_status: item.text_status,
  privat: item.privat,
  quote: item.quote,
  quoted_reaction_id: item.quoted_reaction_id,
  media: item.media,
  text_msg: item.text_msg,
  text_msg_translation: item.text_msg_translation,
  rang: item.rang,
  active: item.active,
  like_you: item.like_you,
  like_count: item.like_count,
  viewed: item.viewed || false,  // явное значение по умолчанию
  viewedAt: item.viewedAt || null
});

const ReactionLine = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const onboarding = useOnboarding();
  const [lessonScore, setLessonScore] = useState(4);
  const readerEntry = location.state?.reactionEntry || location.key;
  const readerEntryRef = useRef(readerEntry);
  readerEntryRef.current = readerEntry;
  const navigationState = useRef(location.state);
  navigationState.current = location.state;
  const refreshKey = location.state?.feedRefresh;
  const initialRefreshKey = useRef(refreshKey);
  const feedGeneration = useRef(0);
  const params = useParams();
  const unsubscribeEntry = routePath(location.pathname)?.replace(/\/$/, '') === '/unsubscribe';
  const entryId = Number(params.id) || (unsubscribeEntry ? UNSUBSCRIBE_CARD_ID : 0);
  const resolvedLanding = useRef(null);
  const profileUsername = params.username;
  const profileFeed = useRef(null);
  const [profileIds, setProfileIds] = useState([]);
  const [loadingAdjacent, setLoadingAdjacent] = useState(false);
  const [adjacentError, setAdjacentError] = useState(null);
  const pendingStory = readFeedStoryPreview(location.state, params.id);
  const viewerContext = feedViewerContext();
  const initialFeed = useRef();
  if (initialFeed.current === undefined) initialFeed.current = recallFeedFromSearch(location.state?.searchFeedToken, entryId);
  const [currentNews, setCurrentNews] = useState(initialFeed.current?.currentNews || null);
  const [loading, setLoading] = useState(true);
  const [viewed, setViewed] = useState(initialFeed.current?.viewed || []);
  const [unViewed, setUnViewed] = useState(initialFeed.current?.unViewed || []);
  const [transition, setTransition] = useState({
    active: false,
    direction: null,
    nextNews: null,
    prevNews: null
  });
  const adjacentNews = useMemo(() => {
    if (!profileUsername) return adjacentNewsFor(currentNews, viewed, unViewed);
    const index = profileIds.indexOf(currentNews?.id);
    return { prev: index > 0 ? { id: profileIds[index - 1] } : null,
      next: index >= 0 && profileIds[index + 1] ? { id: profileIds[index + 1] } : null };
  }, [currentNews, viewed, unViewed, profileUsername, profileIds]);
  const pagination = useRef(null);
  const firstPage = useRef(null);
  const touchStart = useRef(null);
  const lessonWheel = useRef({ at: 0, distance: 0, consumed: false });
  const swipePending = useRef(false);
  const swipeQueue = useRef([]);
  const buffer = useRef({ viewed, unViewed });
  const exhausted = useRef(false);
  const reportedViews = useRef(new Set());
  const updateBuffer = useCallback((next) => {
    buffer.current = next;
    setViewed(next.viewed); setUnViewed(next.unViewed);
  }, []);
  const mounted = useRef(true);
  const [loadError, setLoadError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const requestKey = JSON.stringify([readerEntry, refreshKey, retryCount, viewerContext, profileUsername]);
  const [loadedContext, setLoadedContext] = useState(() => initialFeed.current
    ? { viewer: viewerContext, requestKey, readerEntry } : null);
  const busy = loading || loadedContext?.requestKey !== requestKey;
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const isSwiping = useRef(false);
  const [pageTitle, setPageTitle] = useState('');
  const [pageDescription, setPageDescription] = useState('');
  const [swiped, setSwiped] = useState(() => initialFeed.current?.swiped ?? !readReactionReader(readerEntry, params.id)?.read);
  const [cardOverflows, setCardOverflows] = useState(false);

  const containerRef = useRef(null);
  const currentNewsRef = useRef(null);
  const ref_header = useRef(null);
  const ref_footer = useRef(null);
  const pendingOtherCard = busy && Number(params.id) > 0 && Number(params.id) !== currentNews?.id;
  const unavailable = loadError || !currentNews || loadedContext?.viewer !== viewerContext || pendingOtherCard;
  const reading = !unavailable && !swiped;
  const { contentHeight: height_content, contentTop, headerHeight } = usePageLayout(ref_header, ref_footer, {
    headerVisible: !reading, footerVisible: !reading,
  });
  const duration_animation = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0 : 280;
  const { source, storyId } = location.state || {};
  const [isScrollMode, setIsScrollMode] = useState(initialFeed.current?.isScrollMode || false);
  const restoredScroll = useRef(initialFeed.current?.scrollTop ?? null);
  const overflowObserver = useRef(null);

  // One pagination request per feed generation. Rapid swipes must not queue
  // overlapping pages; an old account/filter reply cannot fill the new buffer.
  const hideLoadReactions = useCallback(() => {
    const generation = feedGeneration.current;
    if (pagination.current?.generation === generation) return pagination.current.promise;
    const work = { generation, controller: new AbortController() };
    pagination.current = work;
    const isCurrent = () => mounted.current && generation === feedGeneration.current && viewerContext === feedViewerContext();
    work.promise = (async () => {
      try {
        if (firstPage.current?.generation === generation) {
          await firstPage.current.promise;
          if (!isCurrent()) return false;
          if (buffer.current.unViewed.some(item => !item.lesson)) return true;
        }
        const response = await reactionsLoad({ area: getLocalStorage(config.area), source, storyId }, { signal: work.controller.signal });
        if (!isCurrent() || !response.success || !Array.isArray(response.list)) return false;
        const { viewed, unViewed } = buffer.current;
        const existing = new Set([...viewed, ...unViewed].map(item => item.id));
        const added = response.list.filter(item => !existing.has(item.id)).map(adaptReactionItem);
        exhausted.current = response.list.length === 0;
        // Publish the page immediately. IndexedDB is persistence, not the
        // source of truth for each animation or a reason to lose a gesture.
        updateBuffer({ viewed, unViewed: [...unViewed, ...added] });
        db.transaction('rw', db.news, async () => {
          if (isCurrent()) await db.news.bulkPut(added);
        }).catch(() => {});
        return true;
      } catch (error) {
        // Pagination failure never replaces the visible card with an error.
        if (isCurrent()) console.error('Could not refresh the feed:', error);
        return false;
      } finally {
        if (pagination.current === work) pagination.current = null;
      }
    })();
    return work.promise;
  }, [source, storyId, viewerContext, updateBuffer]);

  useLayoutEffect(() => {
    const card = currentNewsRef.current?.querySelector('.reaction-card');
    if (card && restoredScroll.current !== null) {
      card.scrollTop = restoredScroll.current;
      restoredScroll.current = null;
    }
  });

  // A short screen must keep native card scrolling and still offer feed navigation.
  // Loading and transitions replace the card. Ordinary renders must not force
  // layout or rebuild all observers; its existing observer sees size changes.
  useLayoutEffect(() => {
    const card = currentNewsRef.current?.querySelector('.reaction-card');
    if (overflowObserver.current?.card === card) return;
    overflowObserver.current?.dispose();
    let disposed = false;
    let waitingForMotion = false;
    const measure = () => {
      if (disposed || waitingForMotion) return;
      // FLIP translations affect scrollHeight without changing layout. A
      // temporary overflow must not add navigation arrows and shrink the card.
      const motion = card?.getAnimations?.({ subtree: true })
        .filter(animation => animation.effect?.getTiming().iterations !== Infinity
          && (animation.playState === 'running' || animation.playState === 'paused')) || [];
      if (motion.length) {
        waitingForMotion = true;
        Promise.allSettled(motion.map(animation => animation.finished)).then(() => {
          waitingForMotion = false;
          measure();
        });
        return;
      }
      setCardOverflows(Boolean(card && card.scrollHeight > card.clientHeight + 1));
    };
    measure();
    const observer = card && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (card) {
      observer?.observe(card);
      Array.from(card.children).forEach(child => observer?.observe(child));
    }
    overflowObserver.current = { card, dispose: () => { disposed = true; observer?.disconnect(); } };
  });
  useLayoutEffect(() => () => {
    overflowObserver.current?.dispose();
    overflowObserver.current = null;
  }, []);

  // Keep a usable card in place while changing the feed filter. It is inert
  // until the new request succeeds; another account/language never sees it.
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const generation = ++feedGeneration.current;
    const isCurrent = () => !cancelled && viewerContext === feedViewerContext();
    const init = async () => {
      setLoading(true);
      setLoadError(false);
      setAdjacentError(null);
      setLoadingAdjacent(false);
      profileFeed.current = null;
      setTransition({ active: false, direction: null, nextNews: null, prevNews: null });
      swipePending.current = false;
      swipeQueue.current = [];
      exhausted.current = false;
      reportedViews.current.clear();
      const progress = readProgress();
      const teaching = !unsubscribeEntry && onboarding.eligible && !getUser()?.id && !params.id && !refreshKey
        && !profileUsername && !progress.completed;
      if (teaching) {
        // Show bundled lessons immediately while real public content preloads.
        // Tutorial ids never reach IndexedDB, public routes or API mutations.
        updateBuffer({ viewed: lessonCards.slice(0, progress.step + 1), unViewed: lessonCards.slice(progress.step + 1) });
        setCurrentNews(lessonCards[progress.step]);
        setLoadedContext({ viewer: viewerContext, requestKey, readerEntry });
        setSwiped(true); setIsScrollMode(false); setLoading(false);
      }
      let retainedLanding = false;
      try {
        const reactionId = entryId;
        const saved = refreshKey === initialRefreshKey.current && retryCount === 0
          && recallFeedFromSearch(navigationState.current?.searchFeedToken, reactionId);
        if (profileUsername) {
          const matching = saved?.profileUsername === profileUsername ? saved : null;
          const feed = await createProfileFeed(profileUsername, controller.signal, matching?.profileFeed);
          const card = matching?.currentNews || await feed.load(reactionId);
          if (!isCurrent()) return;
          if (!card) {
            navigate(url(`${APP_ROUTES.USER}/${encodeURIComponent(profileUsername)}`), { replace: true });
            return;
          }
          profileFeed.current = feed;
          setProfileIds(feed.ids);
          updateBuffer({ viewed: [], unViewed: [] });
          const read = matching ? !matching.swiped : Boolean(readReactionReader(readerEntry, reactionId)?.read);
          setSwiped(!read); setIsScrollMode(read);
          restoredScroll.current = matching?.scrollTop ?? null;
          setCurrentNews(card);
          setLoadedContext({ viewer: viewerContext, requestKey, readerEntry });
          feed.prefetch(card.id);
          return;
        }
        if (saved) {
          // Restore the DOM immediately; rebuilding the local browsing buffer
          // is not a reason to flash a blank card or refetch the same page.
          updateBuffer({ viewed: saved.viewed, unViewed: saved.unViewed });
          setSwiped(saved.swiped);
          setIsScrollMode(saved.isScrollMode);
          restoredScroll.current = saved.scrollTop;
          setCurrentNews(saved.currentNews);
          setLoadedContext({ viewer: viewerContext, requestKey, readerEntry });
          db.transaction('rw', db.news, async () => {
            if (!isCurrent()) return;
            await db.news.clear();
            await db.news.bulkPut([...saved.viewed, ...saved.unViewed].filter(item => item.id > 0));
          }).catch(() => {});
          return;
        }
        // Start the network before IndexedDB work. The old buffer remains
        // intact on a failed request and an obsolete reply cannot clear it.
        let landing = null;
        if (unsubscribeEntry) {
          // Resolve locale before loading the real feed. The response survives
          // the language re-render but is never persisted across visits/users.
          const landingKey = JSON.stringify([readerEntry, getUser()?.id || null, retryCount]);
          let response = resolvedLanding.current?.key === landingKey ? resolvedLanding.current.response : null;
          if (!response) {
            const pathLanguage = location.pathname.split('/')[1];
            const language = getUser()?.lng_code || (config.languages.includes(pathLanguage) ? pathLanguage : undefined);
            response = await api.unsubscribeLanding({ language }, { signal: controller.signal });
            if (!isCurrent()) return;
            if (!response.success) throw Error('Landing unavailable');
            resolvedLanding.current = { key: landingKey, response };
          }
          if (['en', ...config.languages.filter(Boolean)].includes(response.language) && i18n.language !== response.language) {
            await changeLanguageInOrder(i18n, response.language, isCurrent);
            if (!isCurrent()) return;
          }
          landing = unsubscribeCard(response);
          if (landing) {
            retainedLanding = true;
            updateBuffer({ viewed: [landing], unViewed: [] });
            setCurrentNews(landing);
            setLoadedContext({ viewer: viewerContext, requestKey, readerEntry });
            setSwiped(true); setIsScrollMode(false); setLoading(false);
          }
        }
        const returningToCard = readFeedStoryPreview(navigationState.current, reactionId);
        const promise = reactionId > 0 && (!refreshKey || returningToCard)
          ? reactionLoad({ id: reactionId }, { signal: controller.signal })
          : reactionsLoadRefresh({ area: getLocalStorage(config.area) }, { signal: controller.signal });
        firstPage.current = { generation, promise };
        const response = await promise;
        if (firstPage.current?.generation === generation) firstPage.current = null;
        if (!isCurrent()) return;
        if (response.success && Array.isArray(response.list)) {
          const records = response.list.map(adaptReactionItem);
          if (records[0] && !teaching && !landing) records[0] = { ...records[0], viewed: true, viewedAt: new Date() };
          db.transaction('rw', db.news, async () => {
            if (!isCurrent()) return;
            await db.news.clear();
            await db.news.bulkPut(records);
          }).catch(() => {});
          if (!isCurrent()) return;
          if (teaching) {
            updateBuffer({ viewed: buffer.current.viewed, unViewed: [...buffer.current.unViewed, ...records] });
            return;
          }
          if (landing) {
            updateBuffer({ viewed: buffer.current.viewed, unViewed: [...buffer.current.unViewed, ...records] });
            return;
          }
          updateBuffer({ viewed: records.slice(0, 1), unViewed: records.slice(1) });
          if (!isCurrent()) return;
          const reader = readReactionReader(readerEntry, reactionId);
          setSwiped(!reader?.read);
          setIsScrollMode(Boolean(reader?.read));
          setCurrentNews(records[0] || null);
          setLoadedContext({ viewer: viewerContext, requestKey, readerEntry });
          if (records.length && records.length <= 6) hideLoadReactions();
        } else if (!teaching && !landing) {
          if (response.statusCode === 404) navigate(url(APP_ROUTES.PAGE404), { replace: true });
          else if (response.statusCode === 403) navigate(url(APP_ROUTES.PAGE403), { replace: true });
          else setLoadError(true);
        }
      } catch {
        // A background feed failure must not replace a usable landing card.
        if (isCurrent() && !teaching && !retainedLanding) setLoadError(true);
      } finally {
        if (firstPage.current?.generation === generation) firstPage.current = null;
        if (isCurrent()) setLoading(false);
      }
    };
    init();
    return () => {
      cancelled = true;
      controller.abort();
      swipeQueue.current = [];
      if (pagination.current?.generation === generation) pagination.current.controller.abort();
    };
    // params.id/requestKey change with each URL replacement while swiping;
    // only a new entry/filter/account should reinitialize this browsing session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryCount, refreshKey, readerEntry, viewerContext, profileUsername, hideLoadReactions, updateBuffer]);

  useEffect(() => {
    if (busy || !currentNews?.id) return;
    if (currentNews.lesson) {
      saveProgress(currentNews.lesson.step - 1);
      setPageTitle('Welcome to wiw');
      setPageDescription('How it works');
      return;
    }
    if (currentNews.system === 'unsubscribe') {
      if (location.pathname !== url('/unsubscribe') || location.search || location.hash
        || navigationState.current?.reactionEntry !== readerEntryRef.current) {
        navigate(url('/unsubscribe'), { replace: true, state: {
          ...navigationState.current, reactionEntry: readerEntryRef.current,
        } });
      }
      setPageTitle(currentNews.story.title);
      setPageDescription('Your opponents won’t miss you. Move the slider. Have your say.');
      return;
    }
    const state = navigationState.current;
    const preview = readFeedStoryPreview(state, currentNews.id);
    if (Number(params.id) !== currentNews.id || state?.reactionEntry !== readerEntryRef.current
      || preview?.title !== currentNews.story.title) {
      navigate(url(opinionPath(currentNews.id, profileUsername)), { replace: true, state: {
        ...state, reactionEntry: readerEntryRef.current,
        feedStoryPreview: createFeedStoryPreview(currentNews.id, currentNews.story, state?.feedRefresh ?? null),
      } });
    }
    setPageTitle(currentNews.story.title);
    setPageDescription(currentNews.story.title);
  }, [currentNews, busy, navigate, profileUsername, params.id, location.pathname, location.search, location.hash]);



  // Обработчик свайпа
  const handleSwipe = useCallback(async (direction, queued = false) => {
    if (busy || !swiped) return;
    if (swipePending.current || transition.active) {
      // Retain deliberate gestures made during motion. Bound the queue so
      // an extreme burst cannot leave the feed moving long after touch ends.
      if (swipeQueue.current.length < 3) swipeQueue.current.push(direction);
      return;
    }
    swipePending.current = true;
    setAdjacentError(null);
    const generation = feedGeneration.current;
    const isCurrent = () => mounted.current && generation === feedGeneration.current && viewerContext === feedViewerContext();
    try {

    // Получаем свежие adjacentNews перед использованием
    const freshAdjacent = adjacentNewsFor(currentNews, buffer.current.viewed, buffer.current.unViewed);
    let targetNews;
    if (profileUsername) {
      setLoadingAdjacent(true);
      targetNews = await profileFeed.current.move(currentNews.id, direction === 'up' ? 1 : -1);
      if (!isCurrent()) return;
      setProfileIds(profileFeed.current.ids);
      setLoadingAdjacent(false);
    } else {
      targetNews = direction === 'up' ? freshAdjacent.next : freshAdjacent.prev;
      if (!targetNews && direction === 'up' && !exhausted.current) {
        setLoadingAdjacent(true);
        const loaded = await hideLoadReactions();
        if (!isCurrent()) return;
        if (!loaded) { swipeQueue.current = []; setAdjacentError(direction); return; }
        targetNews = adjacentNewsFor(currentNews, buffer.current.viewed, buffer.current.unViewed).next;
      }
    }

    if (!isCurrent()) return;
    if (!targetNews) {
      swipeQueue.current = [];
      if (direction === 'up' && currentNews?.lesson?.step === lessonCards.length && exhausted.current) {
        saveProgress(lessonCards.length - 1, true);
        onboarding.dismiss();
        updateBuffer({ viewed: [], unViewed: [] });
        setCurrentNews(null);
        return;
      }
      message.info({ content: direction === 'up' ? t('post.finish_title') : t('post.first_reaction') });
      return;
    }

    // Queued flicks catch up briskly instead of accumulating animation lag.
    const motionDuration = queued && duration_animation ? 180 : duration_animation;
    setTransition({
      duration: motionDuration,
      active: true,
      direction,
      nextNews: direction === 'up' ? targetNews : null,
      prevNews: direction === 'down' ? targetNews : null
    });

    // Ждем завершения анимации
    await new Promise(resolve => setTimeout(resolve, motionDuration));
    if (!isCurrent()) return;

    // Commit the visual transition without waiting for storage or analytics.
    setCurrentNews(targetNews);
    if (currentNews.lesson && !targetNews.lesson) {
      saveProgress(lessonCards.length - 1, true);
      // Completion removes the tutorial from backward navigation as well.
      updateBuffer({ viewed: buffer.current.viewed.filter(item => !item.lesson),
        unViewed: buffer.current.unViewed.filter(item => !item.lesson) });
    }
    if (profileUsername) {
      profileFeed.current.prefetch(targetNews.id);
    } else if (direction === 'up') {
      const { viewed, unViewed } = buffer.current;
      const leaving = unViewed.find(item => item.id === currentNews.id);
      if (leaving) {
        const patch = { viewed: true, viewedAt: new Date() };
        updateBuffer({ viewed: [...viewed, { ...leaving, ...patch }],
          unViewed: unViewed.filter(item => item.id !== leaving.id) });
        if (leaving.id > 0) db.transaction('rw', db.news, async () => {
          if (isCurrent()) await db.news.update(leaving.id, patch);
        }).catch(() => {});
      }
      if (currentNews.id > 0 && !reportedViews.current.has(currentNews.id)) {
        reportedViews.current.add(currentNews.id);
        Promise.resolve(reactionView({ id: currentNews.id })).catch(() => {});
      }
      if (!targetNews.lesson && buffer.current.unViewed.length <= 6 && !exhausted.current) hideLoadReactions();
    }

    } catch (error) {
      if (isCurrent()) { swipeQueue.current = []; setAdjacentError(direction); }
    } finally {
      if (isCurrent()) {
        swipePending.current = false;
        setLoadingAdjacent(false);
        setTransition({ active: false, direction: null, nextNews: null, prevNews: null });
      }
    }
  }, [transition.active, currentNews, swiped, duration_animation, t, busy, viewerContext, profileUsername, hideLoadReactions, updateBuffer, onboarding]);

  // Drain only after React commits the new current card, using its fresh
  // neighbors. Filter/account changes and leaving the reader clear the queue.
  useEffect(() => {
    if (!swiped || busy) { swipeQueue.current = []; return; }
    if (!swipePending.current && !transition.active && swipeQueue.current.length) {
      handleSwipe(swipeQueue.current.shift(), true);
    }
  }, [handleSwipe, transition.active, busy, swiped]);

  // Обработчики свайпа
  // Выносим обработчики за пределы handleSwipeEvents
  const handleTouchStart = useCallback((e) => {
    isSwiping.current = false;
    touchStart.current = null;
    if (isScrollMode || e.touches.length !== 1 ||
        !canStartSwipe(e.target, containerRef.current, window.visualViewport?.scale || 1)) return;
    const touch = e.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY, time: e.timeStamp };
    isSwiping.current = true;
  }, [isScrollMode]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length !== 1 || (window.visualViewport?.scale || 1) > 1.01) {
      isSwiping.current = false;
      return;
    }
    if (!isSwiping.current || isScrollMode || !touchStart.current) return;
    const touch = e.touches[0];
    const direction = getSwipeDirection(touchStart.current, { x: touch.clientX, y: touch.clientY }, 12);
    if (direction && e.cancelable) e.preventDefault();
  }, [isScrollMode]);

  const handleTouchEnd = useCallback(async (e) => {
    if (!isSwiping.current || !containerRef.current || isScrollMode || !touchStart.current) return;
    isSwiping.current = false;
    if (e.touches.length || !e.changedTouches.length) return;
    const touch = e.changedTouches[0];
    const direction = getSwipeDirection(touchStart.current, { x: touch.clientX, y: touch.clientY, time: e.timeStamp });
    touchStart.current = null;
    if (direction) await handleSwipe(direction);
  }, [handleSwipe, isScrollMode]);

  const handleTouchCancel = useCallback(() => {
    isSwiping.current = false;
    touchStart.current = null;
  }, []);

  const handleLessonWheel = useCallback(event => {
    if (!currentNews?.lesson || !canStartSwipe(event.target, containerRef.current)) return;
    const gesture = lessonWheel.current;
    const now = Date.now();
    if (now - gesture.at > 180) { gesture.distance = 0; gesture.consumed = false; }
    gesture.at = now;
    if (event.cancelable) event.preventDefault();
    gesture.distance += event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? height_content : 1);
    if (!gesture.consumed && Math.abs(gesture.distance) > 45 && Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      gesture.consumed = true;
      handleSwipe(gesture.distance > 0 ? 'up' : 'down');
    }
  }, [currentNews, handleSwipe, height_content]);

  // Переписываем useEffect для обработки событий
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);
    container.addEventListener('touchcancel', handleTouchCancel);
    container.addEventListener('wheel', handleLessonWheel, { passive: false });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchCancel);
      container.removeEventListener('wheel', handleLessonWheel);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel, handleLessonWheel]);

  const changeSwipe = useCallback((val) => {
    setSwiped(val);
    setIsScrollMode(!val);
  }, []);

  const rememberFeed = (updated = currentNews) => {
    const replaceCard = item => item.id === updated?.id ? { ...item, ...updated } : item;
    const profileSnapshot = profileUsername && profileFeed.current?.snapshot();
    const searchFeedToken = updated ? rememberFeedForSearch({
      currentNews: updated, viewed: viewed.map(replaceCard), unViewed: unViewed.map(replaceCard), swiped, isScrollMode,
      scrollTop: currentNewsRef.current?.querySelector('.reaction-card')?.scrollTop || 0,
      ...(profileSnapshot ? { profileUsername, profileFeed: {
        ...profileSnapshot,
        cards: profileSnapshot.cards.map(([id, card]) => [id, replaceCard(card)]),
      } } : {}),
    }) : undefined;
    return {
      reactionEntry: readerEntryRef.current, feedRefresh: refreshKey,
      source, storyId, searchFeedToken,
      ...(profileUsername ? { profileUsername } : {}),
      feedStoryPreview: createFeedStoryPreview(currentNews.id, currentNews.story, refreshKey ?? null),
    };
  };

  const openSearch = () => {
    if (busy || transition.active) return;
    if (currentNews.lesson) { onboarding.dismiss(); navigate(url(APP_ROUTES.SEARCH)); return; }
    const origin = {
      pathname: currentNews.system === 'unsubscribe' ? url('/unsubscribe') : url(`${APP_ROUTES.REACTION}/${currentNews.id}`), search: '',
      state: { ...location.state, ...rememberFeed() },
    };
    // Put the return token on both entries, so the browser's Back works too.
    navigate(origin.pathname, { replace: true, state: origin.state });
    navigate(url(APP_ROUTES.SEARCH), { state: { searchOrigin: origin, fromSearchEntry: true } });
  };

  const lessonTarget = useMemo(() => currentNews?.lesson
    ? viewed.find(item => !item.lesson) || unViewed.find(item => !item.lesson) : null,
  [viewed, unViewed, currentNews]);
  const openFromLesson = action => {
    if (action === 'login') {
      onboarding.dismiss(); navigate(`${url('/login')}?from=${encodeURIComponent(url('/'))}`); return;
    }
    if (!lessonTarget) return;
    onboarding.dismiss();
    const card = lessonTarget;
    const state = { reactionEntry: readerEntry, searchFeedToken: rememberFeedForSearch({
      currentNews: card, viewed: [card], unViewed: [...viewed, ...unViewed].filter(item => !item.lesson && item.id !== card.id),
      swiped: true, isScrollMode: false, scrollTop: 0,
    }), feedStoryPreview: createFeedStoryPreview(card.id, card.story, null) };
    const from = url(opinionPath(card.id));
    // Back returns to this real event, never to a synthetic tutorial URL.
    navigate(from, { replace: true, state });
    if (action === 'comment') {
      flushSync(() => navigate(url(`/news/${card.story.id}/opinion`), { state: { focusComposer: true, story: card.story } }));
      document.querySelector('.comment-editor-container .ProseMirror[contenteditable="true"]')?.focus({ preventScroll: true });
    } else if (action === 'repost') {
      navigate(`${url('/login')}?from=${encodeURIComponent(url(`/news/${card.story.id}/opinion`) + `?quote=${card.id}`)}`);
    } else {
      navigate(url(`/news/${card.story.id}/${action}`), { state: {
        from, storyPreview: card.story, storyFeedReturn: { storyId: card.story.id, reactionId: card.id, state },
      } });
    }
  };

  const profileHeader = profileUsername && <WiwHeaderTitle preserveCase
    title={(!unavailable && currentNews.user.user_name) || profileUsername}
    onBack={() => navigate(url(`${APP_ROUTES.USER}/${encodeURIComponent(profileUsername)}`), { replace: true })} />;
  if (unavailable) return <Layout className="reaction-line reaction-line--unavailable">
    <Header key="feed-header" ref={ref_header} className="header">
      {profileHeader || <PostFilter />}
    </Header>
    <Content className="content-layot reaction-line-state" style={{ height: height_content, top: contentTop }}>
      {pendingStory && <div className="wrapper-padding"><StoryTitle
        reactionId={Number(params.id)} storyId={pendingStory.id} title={pendingStory.title} /></div>}
      <div className="reaction-line-state__body">
      {loading || (busy && !loadError) ? <BrandLoader className="reaction-line-loader" label={t('brand:loading')} /> :
      loadError ? <AppFailure onRetry={() => {
          setLoading(true); setLoadError(false); setRetryCount(value => value + 1);
        }} /> : <p role="status">{t('post.finish_title')}</p>
      }
      </div>
    </Content>
    <Footer key="feed-footer" ref={ref_footer}><ButtonPanelReaction /></Footer>
  </Layout>;

  return (
    <Layout className="reaction-line">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {currentNews.system === 'unsubscribe' && <meta name="robots" content="noindex, nofollow" />}
      </Helmet>
      <Header key="feed-header" ref={ref_header}
        {...mutedProps(currentNews.lesson?.step < 4, `header${swiped ? '' : ' feed-header--reading'}`)}
        aria-hidden={!swiped || currentNews.lesson?.step < 4}
        inert={!swiped || currentNews.lesson?.step < 4 ? '' : undefined}
        style={{ '--feed-header-height': `${headerHeight}px` }}>
        {profileHeader || <PostFilter
          onSearch={openSearch}
          searchDisabled={busy || transition.active}
          filtersDisabled={transition.active}
        />}
      </Header>
      {(busy || loadingAdjacent) && <BrandLoader className="reaction-line-loader brand-loader--refresh" label={t('brand:loading')} />}
      <div
        ref={containerRef}
        style={{ height: height_content + 'px', top: contentTop + 'px' }}
        className={`news-container${isScrollMode ? ' news-container-reading' : ''}`}
        tabIndex={currentNews.lesson ? 0 : undefined}
        aria-label={currentNews.lesson ? 'How it works' : undefined}
        onKeyDown={event => {
          if (!currentNews.lesson || event.target !== event.currentTarget) return;
          if (['ArrowDown', 'PageDown', 'ArrowUp', 'PageUp'].includes(event.key)) {
            event.preventDefault(); handleSwipe(['ArrowDown', 'PageDown'].includes(event.key) ? 'up' : 'down');
          }
        }}
        aria-busy={busy || loadingAdjacent || transition.active}
        inert={busy ? '' : undefined}
      >
        {/* The incoming card becomes the current card with the same key/DOM.
            Remounting it at the end of a swipe flashes avatars and media. */}
        {[transition.prevNews, currentNews, transition.nextNews].filter(Boolean).map(news => {
          const current = news.id === currentNews.id;
          const motion = transition.active ? ` news-screen--${current ? 'exit' : 'enter'}-${transition.direction}` : '';
          return <div key={news.id} ref={current ? currentNewsRef : undefined}
            className={`news-screen ${current ? 'current-news' : transition.direction === 'up' ? 'next-news' : 'prev-news'}${motion}`}
            inert={transition.active ? '' : undefined}
            aria-hidden={!current} style={{ '--feed-motion-duration': `${transition.duration ?? duration_animation}ms` }}>
            <Content className="content-layot" style={{ height: height_content }}>
              {news.lesson ? <OnboardingCard lesson={news.lesson} score={lessonScore} onScore={setLessonScore}
                onOpen={openFromLesson} targetReady={Boolean(lessonTarget)} /> :
                <ReactionContent key={`${loadedContext.readerEntry}:${news.id}`} reaction={news} scope={news.scope} profileUsername={profileUsername} readerEntry={loadedContext.readerEntry} onLeaveFeed={current && !busy ? rememberFeed : undefined}
                  onChangeSwipe={changeSwipe} swiped={current ? swiped : true} />}
            </Content>
          </div>;
        })}
      </div>
      <Footer key="feed-footer" ref={ref_footer} className={reading ? 'feed-footer--reading' : undefined}
        aria-hidden={reading} inert={reading ? '' : undefined}>
        {adjacentError && <LoadFailure onRetry={() => handleSwipe(adjacentError)} />}
        {swiped && cardOverflows &&
          <nav className="reaction-feed-navigation" aria-label={t('reaction.navigation', { defaultValue: 'Reaction navigation' })}>
            <Button aria-label={t('reaction.previous', { defaultValue: 'Previous reaction' })}
              icon={<ChevronUpIcon className="nav-icon" />} disabled={!adjacentNews.prev || busy || transition.active}
              onClick={() => handleSwipe('down')} />
            <Button aria-label={t('reaction.next', { defaultValue: 'Next reaction' })}
              icon={<ChevronDownIcon className="nav-icon" />} disabled={!adjacentNews.next || busy || transition.active}
              onClick={() => handleSwipe('up')} />
          </nav>
        }
        <div {...mutedProps(currentNews.lesson?.step < 4)}><ButtonPanelReaction /></div>
        <span className='reaction-link-prev-next'>
          {adjacentNews.prev?.id > 0 &&
            <Link to={url(opinionPath(adjacentNews.prev.id, profileUsername))}>next - {adjacentNews.prev.id}</Link>
          }
          {adjacentNews.next?.id > 0 &&
            <Link to={url(opinionPath(adjacentNews.next.id, profileUsername))}>prev - {adjacentNews.next.id}</Link>
          }
        </span>
      </Footer>
    </Layout>
  );
};

export default ReactionLine;
